import * as THREE from 'three';

//=============================================================================
// FLYIN CARS INIT
//=============================================================================
class Cars extends THREE.Object3D {
  /**
   * paths: array of { start: THREE.Vector3, end: THREE.Vector3, count: number (optional) }
   * If omitted, a single default path is used.
   */
  constructor(scene,paths = null, defaultCount = 50,loadingManager) {
    super();

    // Backwards-compatible single default path
    if (!paths) {
      paths = [{
        start: new THREE.Vector3(0, 25, -20),
        end: new THREE.Vector3(5, 25, 20),
        count: defaultCount
      }];
    }

    // ensure start/end are clones
    this.paths = paths.map(p => ({
      start: p.start.clone(),
      end: p.end.clone(),
      count: Math.max(1, p.count || defaultCount)
    }));

    // total instances across all paths
    this.totalCount = this.paths.reduce((s, p) => s + p.count, 0);

    // single shared geometry/material for all cars
    const carGeometry = new THREE.BoxGeometry(0.3, 0.15, 0.8);
    const carMaterial = new THREE.MeshStandardMaterial({
      color: 0x000000,
      emissive: 0x00ffff,
      emissiveIntensity: 0.6,
      roughness: 0.9,
      metalness: 0.0
    });
    carMaterial.fog = false; // let global fog handle distance; can be tweaked

    // single instanced mesh for all paths
    this.instanced = new THREE.InstancedMesh(carGeometry, carMaterial, this.totalCount);
    this.instanced.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.add(this.instanced);

    // per-instance arrays
    this.pathId = new Uint16Array(this.totalCount);
    this.progress = new Float32Array(this.totalCount); // 0..1
    this.speed = new Float32Array(this.totalCount);
    this.lateralOffset = new Float32Array(this.totalCount);
    this.yaw = new Float32Array(this.totalCount);

    // per-path precomputed direction/length/perp (updated when endpoints change)
    this._pathInfo = this.paths.map(p => {
      const dir = new THREE.Vector3().subVectors(p.end, p.start).setY(0);
      const length = dir.length();
      if (length > 1e-6) dir.normalize();
      const perp = new THREE.Vector3(-dir.z, 0, dir.x);
      return { dir, length, perp };
    });

    // initialize instances: distribute along each path
    const dummy = new THREE.Object3D();
    let idx = 0;
    for (let pi = 0; pi < this.paths.length; pi++) {
      const p = this.paths[pi];
      const info = this._pathInfo[pi];
      for (let j = 0; j < p.count; j++, idx++) {
        this.pathId[idx] = pi;

        // spacing along path (spread group along path)
        const spacing = (j / p.count) * 0.9 + Math.random() * 0.15;
        this.progress[idx] = spacing;

        // small speed variation
        this.speed[idx] = THREE.MathUtils.lerp(0.85, 1.12, Math.random());

        // lateral scatter (small to keep cars as a bunch)
        this.lateralOffset[idx] = THREE.MathUtils.lerp(0.0, 0.6, Math.random()) * (Math.random() < 0.15 ? 0.25 : 1.0);

        // initial yaw facing path direction + small noise
        const baseYaw = Math.atan2(info.dir.x, info.dir.z);
        this.yaw[idx] = baseYaw + (Math.random() - 0.5) * 0.01;

        // initial position along path
        const pos = new THREE.Vector3().copy(p.start);
        const dist = spacing * (info.length || 1.0);
        pos.addScaledVector(info.dir, dist);
        pos.addScaledVector(info.perp, (Math.random() - 0.5) * this.lateralOffset[idx]);
        pos.y = THREE.MathUtils.lerp(p.start.y, p.end.y, spacing);

        dummy.position.copy(pos);
        dummy.rotation.set(0, this.yaw[idx], 0);
        dummy.updateMatrix();
        this.instanced.setMatrixAt(idx, dummy.matrix);
      }
    }

    this.instanced.instanceMatrix.needsUpdate = true;

    // base movement speed in world units/sec (tweakable)
    this.baseSpeed = 6.0;
    scene.add(this.instanced);
    loadingManager.increment('Shipment containers traversing...')
    return this;
  }

  // optional: update paths (call if you change endpoints)
  setPaths(paths, defaultCount = 50) {
    // simple replacement strategy: remove old instanced and recreate
    this.clear();
    this.paths = paths.map(p => ({
      start: p.start.clone(),
      end: p.end.clone(),
      count: Math.max(1, p.count || defaultCount)
    }));
    const newCars = new Cars(this.paths, defaultCount);
    // copy newCars internals into this
    this.instanced = newCars.instanced;
    this.add(this.instanced);
    this.pathId = newCars.pathId;
    this.progress = newCars.progress;
    this.speed = newCars.speed;
    this.lateralOffset = newCars.lateralOffset;
    this.yaw = newCars.yaw;
    this._pathInfo = newCars._pathInfo;
    this.totalCount = newCars.totalCount;
    this.baseSpeed = newCars.baseSpeed;
  }

  update(delta) {
    if (!this.instanced) return;
    const dummy = new THREE.Object3D();
    let needsUpdate = false;

    // update path info in case endpoints changed externally
    for (let pi = 0; pi < this.paths.length; pi++) {
      const p = this.paths[pi];
      const dir = new THREE.Vector3().subVectors(p.end, p.start).setY(0);
      const length = dir.length();
      if (length > 1e-6) dir.normalize();
      const perp = new THREE.Vector3(-dir.z, 0, dir.x);
      this._pathInfo[pi].dir.copy(dir);
      this._pathInfo[pi].length = length;
      this._pathInfo[pi].perp.copy(perp);
    }

    for (let i = 0; i < this.totalCount; i++) {
      const pi = this.pathId[i];
      const p = this.paths[pi];
      const info = this._pathInfo[pi];

      // advance progress: prog is normalized 0..1 along path
      // use baseSpeed scaled by per-car multiplier and normalized by path length so speed feels in world units
      const progAdvance = (this.baseSpeed * this.speed[i] * delta) / Math.max(1e-6, info.length);
      this.progress[i] += progAdvance;
      if (this.progress[i] >= 1.0) this.progress[i] -= 1.0; // wrap

      const t = this.progress[i];
      // compute position on path
      const pos = new THREE.Vector3().copy(p.start).addScaledVector(info.dir, t * info.length);

      // lateral scattering that peaks mid-path
      const scatterFactor = Math.sin(t * Math.PI);
      // small per-instance wiggle for organic motion
      const wiggle = (Math.sin(i * 12.9898 + t * 6.2831) * 0.5);
      pos.addScaledVector(info.perp, (wiggle - 0.25) * this.lateralOffset[i] * scatterFactor);

      // vertical lerp between start and end
      pos.y = THREE.MathUtils.lerp(p.start.y, p.end.y, t);

      // orientation: face movement direction (sample slightly ahead)
      const forwardT = Math.min(1.0, t + 0.01);
      const forwardPos = new THREE.Vector3().copy(p.start).addScaledVector(info.dir, forwardT * info.length);
      const look = forwardPos.clone().sub(pos);
      look.y = 0;
      if (look.lengthSq() < 1e-6) look.copy(info.dir);
      look.normalize();
      const targetYaw = Math.atan2(look.x, look.z);
      this.yaw[i] = THREE.MathUtils.lerp(this.yaw[i], targetYaw, Math.min(1.0, 10.0 * delta));

      dummy.position.copy(pos);
      dummy.rotation.set(0, this.yaw[i], 0);
      dummy.updateMatrix();
      this.instanced.setMatrixAt(i, dummy.matrix);
      needsUpdate = true;
    }

    if (needsUpdate) this.instanced.instanceMatrix.needsUpdate = true;
  }
}

export default Cars;